import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { hasNavPermission } from '../components/ProtectedRoute';
import { apiErrorText } from '../utils/apiErrors';
import './SleeverControlPage.css';

const DAYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miercoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sabado' },
];

function getIsoWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// Lotes are named "#<number>" — sort by the number, not alphabetically
// ("#10" would otherwise sort before "#2").
function loteNumber(name) {
  return parseInt(String(name).replace(/\D/g, ''), 10) || 0;
}
const sortLotes = (list) => [...list].sort((a, b) => loteNumber(a.name) - loteNumber(b.name));
// The lowest lote number not in use, so a removed lote (e.g. #1) can be added back.
function nextLoteNumber(list) {
  const used = new Set(list.map((l) => loteNumber(l.name)));
  let n = 1;
  while (used.has(n)) n += 1;
  return n;
}

// Picks black or white text so it stays readable against any ribbon swatch color.
function contrastTextColor(hex) {
  if (!hex) return '#1f2d20';
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1f2d20' : '#ffffff';
}

const NOTE_NUMBERS = [1, 2, 3];

export default function SleeverControlPage() {
  const now = new Date();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const canReopenWeeks = hasNavPermission(user, 'can_reopen_weeks');
  const canDeleteLotes = hasNavPermission(user, 'delete_lotes');

  const [companies, setCompanies] = useState([]);
  const [farms, setFarms] = useState([]);
  const [ribbons, setRibbons] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [farmId, setFarmId] = useState(() => searchParams.get('farm') || '');
  const [year, setYear] = useState(() => searchParams.get('year') || now.getFullYear());
  const [weekNumber, setWeekNumber] = useState(() => searchParams.get('week') || getIsoWeek(now));

  const [week, setWeek] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [lotes, setLotes] = useState([]);
  const [bunchSleevers, setBunchSleevers] = useState([]);
  const [entries, setEntries] = useState([]);

  const [loadingWeek, setLoadingWeek] = useState(false);
  const [status, setStatus] = useState(null);
  const [addingLote, setAddingLote] = useState(false);
  const [loteSelections, setLoteSelections] = useState({}); // { [loteId]: bunchSleeverId }
  const [addingToLoteId, setAddingToLoteId] = useState(null);
  const [reopening, setReopening] = useState(false);
  const [locking, setLocking] = useState(false);

  const loadEntries = (weekId) => {
    api.get(`sleever-entries/?week=${weekId}`).then((r) => setEntries(r.data));
  };

  const handleLoadOrCreateWeek = React.useCallback(async (fId, y, w) => {
    if (!fId || !y || !w) return;
    setLoadingWeek(true);
    setStatus(null);
    try {
      const r = await api.get(`sleever-weeks/?farm=${fId}&year=${y}&week_number=${w}`);
      if (r.data.length > 0) {
        setWeek(r.data[0]);
        loadEntries(r.data[0].id);
      } else {
        const created = await api.post('sleever-weeks/', {
          farm: Number(fId), year: Number(y), week_number: Number(w),
        });
        setWeek(created.data);
        setEntries([]);
      }
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo cargar o crear esa semana.' });
    } finally {
      setLoadingWeek(false);
    }
  }, []);

  useEffect(() => {
    Promise.all([api.get('companies/'), api.get('farms/'), api.get('ribbons/')]).then(
      ([companiesRes, farmsRes, ribbonsRes]) => {
        setCompanies(companiesRes.data);
        setFarms(farmsRes.data);
        setRibbons(ribbonsRes.data);

        const linkedFarmId = searchParams.get('farm');
        const linkedFarm = linkedFarmId ? farmsRes.data.find((f) => String(f.id) === String(linkedFarmId)) : null;
        const initialCompanyId = linkedFarm
          ? String(linkedFarm.company)
          : (companiesRes.data.length > 0 ? String(companiesRes.data[0].id) : '');
        setCompanyId(initialCompanyId);

        const farmsInCompany = farmsRes.data.filter((f) => String(f.company) === initialCompanyId);
        const initialFarmId = linkedFarmId || (farmsInCompany.length > 0 ? String(farmsInCompany[0].id) : '');
        setFarmId(initialFarmId);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const farmsInSelectedCompany = farms.filter((f) => String(f.company) === String(companyId));

  const handleCompanyChange = (e) => {
    const newCompanyId = e.target.value;
    setCompanyId(newCompanyId);
    const firstFarm = farms.find((f) => String(f.company) === String(newCompanyId));
    setFarmId(firstFarm ? String(firstFarm.id) : '');
  };

  const loadFarmData = (id) => {
    if (!id) return;
    Promise.all([api.get(`lotes/?farm=${id}`), api.get(`bunch-sleevers/?farm=${id}`)]).then(
      ([lotesRes, bunchSleeversRes]) => {
        setLotes(sortLotes(lotesRes.data));
        setBunchSleevers(bunchSleeversRes.data);
      }
    );
  };

  useEffect(() => {
    setWeek(null);
    setEntries([]);
    loadFarmData(farmId);
  }, [farmId]);

  // Deep-linked from the saved-list page (?farm=&year=&week=) — auto-load once farms exist.
  useEffect(() => {
    const linkedFarm = searchParams.get('farm');
    const linkedYear = searchParams.get('year');
    const linkedWeek = searchParams.get('week');
    if (linkedFarm && linkedYear && linkedWeek && farms.length > 0) {
      handleLoadOrCreateWeek(linkedFarm, linkedYear, linkedWeek);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farms]);

  const handleReopen = async () => {
    setReopening(true);
    setStatus(null);
    try {
      const r = await api.post(`sleever-weeks/${week.id}/reopen/`);
      setWeek(r.data);
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo reabrir la semana.' });
    } finally {
      setReopening(false);
    }
  };

  const handleLockNow = async () => {
    setLocking(true);
    setStatus(null);
    try {
      const r = await api.post(`sleever-weeks/${week.id}/lock/`);
      setWeek(r.data);
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo cerrar la semana.' });
    } finally {
      setLocking(false);
    }
  };

  // Keep the note inputs in step with whichever week is loaded (and with what the server saved).
  useEffect(() => {
    if (!week) { setNoteDrafts({}); return; }
    setNoteDrafts(Object.fromEntries(NOTE_NUMBERS.flatMap((n) => [
      [`note_${n}`, week[`note_${n}`] || ''],
      [`note_${n}_value`, week[`note_${n}_value`] == null ? '' : String(week[`note_${n}_value`])],
    ])));
  }, [week]);

  const handleNoteSave = async (field) => {
    const draft = noteDrafts[field] ?? '';
    const isValue = field.endsWith('_value');
    const value = isValue ? (draft === '' ? null : Number(draft)) : draft.trim();
    const current = week[field] ?? (isValue ? null : '');
    if (value === current) return;
    try {
      const r = await api.patch(`sleever-weeks/${week.id}/`, { [field]: value });
      setWeek(r.data);
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo guardar la observación.' });
    }
  };

  const handleRibbonChange = async (e) => {
    const ribbonId = e.target.value || null;
    const r = await api.patch(`sleever-weeks/${week.id}/`, { ribbon: ribbonId ? Number(ribbonId) : null });
    setWeek(r.data);
  };

  // Adding a lote just auto-numbers it (the lowest free number: #1, #2, …, or a removed one)
  // and starts it empty — bunch sleevers are added to it explicitly via the dropdown below.
  const handleAddLote = async () => {
    setAddingLote(true);
    setStatus(null);
    try {
      const r = await api.post('lotes/', { name: `#${nextLoteNumber(lotes)}`, farm: Number(farmId) });
      setLotes((ls) => sortLotes([...ls, r.data]));
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo agregar el nuevo lote.' });
    } finally {
      setAddingLote(false);
    }
  };

  // Adding a bunch sleever to one specific lote — the same person can be added
  // to as many different lotes as needed, they just can't repeat within one lote.
  const handleAddToLote = async (loteId) => {
    const bunchSleeverId = loteSelections[loteId];
    if (!bunchSleeverId) return;
    setAddingToLoteId(loteId);
    setStatus(null);
    try {
      const res = await api.post('sleever-entries/', {
        week: week.id, lote: loteId, bunch_sleever: Number(bunchSleeverId),
      });
      setEntries((es) => [...es, res.data]);
      setLoteSelections((s) => ({ ...s, [loteId]: '' }));
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo agregar ese enfundador.' });
    } finally {
      setAddingToLoteId(null);
    }
  };

  const handleEntryFieldChange = (entryId, field, value) => {
    setEntries((es) => es.map((e) => (e.id === entryId ? { ...e, [field]: value } : e)));
  };

  const handleEntryFieldSave = async (entryId, field, value) => {
    const num = Math.max(0, Number(value) || 0);
    try {
      const r = await api.patch(`sleever-entries/${entryId}/`, { [field]: num });
      setEntries((es) => es.map((e) => (e.id === entryId ? r.data : e)));
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo guardar ese valor.' });
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('¿Quitar a este trabajador de la hoja?')) return;
    await api.delete(`sleever-entries/${entryId}/`);
    setEntries((es) => es.filter((e) => e.id !== entryId));
  };

  // A lote belongs to the farm: the API refuses to remove one that other weeks have lines in.
  const handleDeleteLote = async (lote) => {
    if (!window.confirm(`¿Eliminar el lote ${lote.name} de la finca? Se eliminarán sus líneas de esta semana.`)) return;
    setStatus(null);
    try {
      await api.delete(`lotes/${lote.id}/`, { params: { week: week.id } });
      setLotes((ls) => ls.filter((l) => l.id !== lote.id));
      setEntries((es) => es.filter((e) => e.lote !== lote.id));
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudo eliminar ese lote.') });
    }
  };

  const entriesByLote = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      if (!map[e.lote]) map[e.lote] = [];
      map[e.lote].push(e);
    });
    // Insertion order (by id), not alphabetical — lets you add bunch sleevers
    // in whatever order makes sense for the sheet.
    Object.values(map).forEach((list) => list.sort((a, b) => a.id - b.id));
    return map;
  }, [entries]);

  const daySum = (day) => entries.reduce((s, e) => s + (e[day] || 0), 0);
  const totalSum = entries.reduce((s, e) => s + (e.total || 0), 0);
  const melizosSum = entries.reduce((s, e) => s + (e.melizos || 0), 0);
  const primeraVuelta = daySum('monday') + daySum('tuesday') + daySum('wednesday');
  const segundaVuelta = daySum('thursday') + daySum('friday') + daySum('saturday');

  const farmName = farms.find((f) => String(f.id) === String(farmId))?.name || '';
  const selectedRibbon = week ? ribbons.find((r) => r.id === week.ribbon) : null;
  const isLocked = !!week?.is_locked;
  const canEdit = !isLocked || canReopenWeeks;
  const isReopened = !isLocked && week?.reopened_until && new Date(week.reopened_until) > new Date();

  return (
    <div className="sleever-root">
      <div className="sleever-header-row">
        <h1 className="sleever-title">🎋 Control de Enfunde</h1>
        <Link to="/enfunde/list" className="sleever-history-link">Ver Historial »</Link>
      </div>

      <div className="sleever-controls">
        <select className="sleever-select" value={companyId} onChange={handleCompanyChange}>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select className="sleever-select" value={farmId} onChange={(e) => setFarmId(e.target.value)}>
          {farmsInSelectedCompany.length === 0 && <option value="">Sin fincas</option>}
          {farmsInSelectedCompany.map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
        <input
          className="sleever-input-small"
          type="number"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          placeholder="Año"
        />
        <input
          className="sleever-input-small"
          type="number"
          value={weekNumber}
          onChange={(e) => setWeekNumber(e.target.value)}
          placeholder="Semana"
        />
        <button
          className="sleever-btn"
          onClick={() => handleLoadOrCreateWeek(farmId, year, weekNumber)}
          disabled={loadingWeek}
        >
          {loadingWeek ? 'Cargando…' : 'Cargar / Crear Semana'}
        </button>
      </div>

      {status && <div className="sleever-status">{status.text}</div>}

      {week && (
        <div className="sleever-sheet">
          <div className="sleever-sheet-header">
            <div className="sleever-sheet-title">HACIENDA {farmName.toUpperCase()}</div>
            <div className="sleever-sheet-sub">Año {week.year} · Control de Enfunde</div>
          </div>

          <div className="sleever-sheet-meta">
            <div><strong>Semana:</strong> {week.week_number}</div>
            <div className="sleever-ribbon-field">
              <strong>Color de Cinta:</strong>
              <select
                value={week.ribbon || ''}
                onChange={handleRibbonChange}
                className="sleever-select-inline"
                disabled={!canEdit}
                style={selectedRibbon ? {
                  backgroundColor: selectedRibbon.hex_color,
                  color: contrastTextColor(selectedRibbon.hex_color),
                  borderColor: selectedRibbon.hex_color,
                  fontWeight: 600,
                } : undefined}
              >
                <option value="">— sin cinta —</option>
                {ribbons.map((r) => (
                  <option key={r.id} value={r.id} style={{ backgroundColor: r.hex_color, color: contrastTextColor(r.hex_color) }}>
                    {r.color}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isLocked && (
            <div className="sleever-lock-banner">
              <span>🔒 Esta semana está cerrada para edición.</span>
              {canReopenWeeks && (
                <button className="sleever-link-btn" onClick={handleReopen} disabled={reopening}>
                  {reopening ? 'Reabriendo…' : '🔓 Reabrir por 48h'}
                </button>
              )}
            </div>
          )}
          {isReopened && (
            <div className="sleever-lock-banner sleever-lock-banner-reopened">
              <span>🔓 Reabierta temporalmente hasta {new Date(week.reopened_until).toLocaleString()}.</span>
              {canReopenWeeks && (
                <button className="sleever-link-btn" onClick={handleLockNow} disabled={locking}>
                  {locking ? 'Cerrando…' : '🔒 Cerrar ahora'}
                </button>
              )}
            </div>
          )}

          <div className="sleever-table-wrap">
            <table className="sleever-table">
              <colgroup>
                <col className="sleever-col-lote" />
                <col className="sleever-col-nombre" />
                {DAYS.map((d) => <col key={d.key} className="sleever-col-day" />)}
                <col className="sleever-col-total" />
                <col className="sleever-col-melizos" />
                <col className="sleever-col-total" />
                <col className="sleever-col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th>Lote</th>
                  <th>Nombre</th>
                  {DAYS.map((d) => <th key={d.key}>{d.label}</th>)}
                  <th>Total</th>
                  <th>Melizos</th>
                  <th>Total por Lote</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lotes.map((lote) => {
                  const loteEntries = entriesByLote[lote.id] || [];
                  const loteTotal = loteEntries.reduce((s, e) => s + (e.total || 0), 0);
                  // Only exclude bunch sleevers already in THIS lote — the same
                  // person can still be picked for any other lote.
                  const usedInLote = new Set(loteEntries.map((e) => e.bunch_sleever));
                  const availableForLote = bunchSleevers.filter((w) => !usedInLote.has(w.id));
                  const rowCount = Math.max(loteEntries.length, 1) + 1; // + the add-row

                  return (
                    <React.Fragment key={lote.id}>
                      {loteEntries.map((entry, idx) => (
                        <tr key={entry.id}>
                          {idx === 0 && (
                            <td rowSpan={rowCount} className="sleever-lote-cell">
                              <div>{lote.name}</div>
                              {canDeleteLotes && (
                                <button
                                  className="sleever-pill-btn sleever-pill-danger sleever-delete-lote-btn"
                                  onClick={() => handleDeleteLote(lote)}
                                  title="Eliminar lote"
                                  disabled={!canEdit}
                                >
                                  Quitar Lote
                                </button>
                              )}
                            </td>
                          )}
                          <td className="sleever-name-cell" title={entry.bunch_sleever_name}>{entry.bunch_sleever_name}</td>
                          {DAYS.map((d) => (
                            <td key={d.key}>
                              <input
                                className="sleever-cell-input"
                                type="number"
                                min="0"
                                value={entry[d.key] === 0 ? '' : entry[d.key]}
                                onChange={(e) => handleEntryFieldChange(entry.id, d.key, e.target.value)}
                                onBlur={(e) => handleEntryFieldSave(entry.id, d.key, e.target.value)}
                                disabled={!canEdit}
                              />
                            </td>
                          ))}
                          <td className="sleever-total-cell">{entry.total}</td>
                          <td>
                            <input
                              className="sleever-cell-input"
                              type="number"
                              min="0"
                              value={entry.melizos === 0 ? '' : entry.melizos}
                              onChange={(e) => handleEntryFieldChange(entry.id, 'melizos', e.target.value)}
                              onBlur={(e) => handleEntryFieldSave(entry.id, 'melizos', e.target.value)}
                              disabled={!canEdit}
                            />
                          </td>
                          {idx === 0 && (
                            <td rowSpan={rowCount} className="sleever-total-cell">{loteTotal}</td>
                          )}
                          <td>
                            <button
                              type="button"
                              className="sleever-pill-btn sleever-pill-danger"
                              onClick={() => handleDeleteEntry(entry.id)}
                              disabled={!canEdit}
                              title="Quitar a este trabajador del lote"
                            >
                              Quitar
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr className={`sleever-add-to-lote-row${loteEntries.length === 0 ? ' sleever-empty-lote' : ''}`}>
                        {loteEntries.length === 0 && (
                          <td rowSpan={1} className="sleever-lote-cell">
                            <div>{lote.name}</div>
                            {canDeleteLotes && (
                              <button
                                className="sleever-pill-btn sleever-pill-danger sleever-delete-lote-btn"
                                onClick={() => handleDeleteLote(lote)}
                                title="Eliminar lote"
                                disabled={!canEdit}
                              >
                                Quitar Lote
                              </button>
                            )}
                          </td>
                        )}
                        {/* Up to Melizos when the lote has lines (Total por Lote spans them); a lote with
                            no lines yet has the whole row, including the action column. */}
                        <td colSpan={loteEntries.length === 0 ? DAYS.length + 5 : DAYS.length + 3}>
                          <select
                            className="sleever-select-inline"
                            value={loteSelections[lote.id] || ''}
                            onChange={(e) => setLoteSelections((s) => ({ ...s, [lote.id]: e.target.value }))}
                            disabled={!canEdit || availableForLote.length === 0}
                          >
                            <option value="">
                              {availableForLote.length === 0 ? 'Todos los trabajadores ya están en este lote' : '— elegir trabajador —'}
                            </option>
                            {availableForLote.map((w) => (
                              <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="sleever-pill-btn sleever-pill-primary"
                            onClick={() => handleAddToLote(lote.id)}
                            disabled={!canEdit || !loteSelections[lote.id] || addingToLoteId === lote.id}
                          >
                            {addingToLoteId === lote.id ? 'Agregando…' : '+ Agregar'}
                          </button>
                          {loteEntries.length === 0 && (
                            <span className="sleever-empty-note">Sin trabajadores en este lote todavía.</span>
                          )}
                        </td>
                        {loteEntries.length > 0 && <td />}
                      </tr>
                    </React.Fragment>
                  );
                })}
                <tr className="sleever-totals-row">
                  <td colSpan={2}>Total</td>
                  {DAYS.map((d) => <td key={d.key}>{daySum(d.key)}</td>)}
                  <td>{totalSum}</td>
                  <td>{melizosSum}</td>
                  <td>{totalSum}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>

          <div className="sleever-table-actions">
            <div className="sleever-inline-form sleever-add-lote-form">
              <button className="sleever-btn-secondary" onClick={handleAddLote} disabled={addingLote || !canEdit}>
                {addingLote ? 'Agregando…' : `+ Agregar Lote #${nextLoteNumber(lotes)}`}
              </button>
            </div>
          </div>

          <div className="sleever-summary">
            <div><strong>Primera Vuelta:</strong> {primeraVuelta}</div>
            <div><strong>Segunda Vuelta:</strong> {segundaVuelta}</div>
            <div><strong>Total:</strong> {totalSum}</div>
          </div>

          <div className="sleever-notes">
            <div className="sleever-notes-title">Observaciones</div>
            {NOTE_NUMBERS.map((n) => (
              <div key={n} className="sleever-note-row">
                <input
                  className="sleever-note-text"
                  placeholder={`Comentario ${n}`}
                  value={noteDrafts[`note_${n}`] ?? ''}
                  onChange={(e) => setNoteDrafts((d) => ({ ...d, [`note_${n}`]: e.target.value }))}
                  onBlur={() => handleNoteSave(`note_${n}`)}
                  disabled={!canEdit}
                  maxLength={255}
                />
                <input
                  className="sleever-note-value"
                  inputMode="numeric"
                  placeholder="0"
                  value={noteDrafts[`note_${n}_value`] ?? ''}
                  onChange={(e) => setNoteDrafts((d) => ({ ...d, [`note_${n}_value`]: e.target.value.replace(/\D/g, '') }))}
                  onBlur={() => handleNoteSave(`note_${n}_value`)}
                  disabled={!canEdit}
                  aria-label={`Valor del comentario ${n}`}
                />
              </div>
            ))}
            <div className="sleever-note-row sleever-notes-total">
              <span>Total</span>
              <span className="sleever-note-value">{week.notes_total}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
